import { useState } from 'react';
import { cn } from '@/utils/classNames';

interface CalendarProps {
  startDate: Date | null;
  endDate: Date | null;
  onDateSelect: (date: Date, isStart: boolean) => void;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const COLORS = {
  selected: '#367BF5',
  rangeBackground: '#EAEAF6',
  today: '#F1F5F9',
  disabled: '#E2E8F0',
  text: '#001C2E',
  dayLabel: '#64748B',
  monthTitle: '#7D8DA6',
} as const;

const Calendar = ({ startDate, endDate, onDateSelect }: CalendarProps) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const daysInMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  ).getDay();

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const getCurrentDayDate = (day: number) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const isSameDay = (date1: Date, date2: Date) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);
    return d1.getTime() === d2.getTime();
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day: number) => {
    const currentDayDate = getCurrentDayDate(day);
    return (startDate && isSameDay(currentDayDate, startDate)) || 
           (endDate && isSameDay(currentDayDate, endDate));
  };

  const isStartDate = (day: number) => {
    if (!startDate) return false;
    return isSameDay(getCurrentDayDate(day), startDate);
  };

  const isEndDate = (day: number) => {
    if (!endDate) return false;
    return isSameDay(getCurrentDayDate(day), endDate);
  };

  const isInRange = (day: number) => {
    if (!startDate || !endDate) return false;
    const currentDayDate = getCurrentDayDate(day);
    return currentDayDate > startDate && currentDayDate < endDate;
  };

  const isPast = (day: number) => {
    const date = getCurrentDayDate(day);
    return date < new Date(new Date().setHours(0, 0, 0, 0));
  };

  return (
    <div className="bg-white" style={{ fontFamily: 'Gilroy-Regular' }}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-normal" style={{ color: COLORS.monthTitle, fontFamily: 'Poppins' }}>
          {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        <div className="flex space-x-1">
          <button
            type="button"
            onClick={handlePrevMonth}
            className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 9L1 5L5 1" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            type="button"
            onClick={handleNextMonth}
            className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L5 5L1 9" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-0">
        {DAY_NAMES.map((day) => (
          <div
            key={day}
            className="h-8 flex items-center justify-center text-xs font-normal"
            style={{ color: COLORS.dayLabel }}
          >
            {day}
          </div>
        ))}

        {Array.from({ length: firstDayOfMonth }).map((_, index) => (
          <div key={`empty-${index}`} className="h-8 w-8 mx-auto" />
        ))}

        {Array.from({ length: daysInMonth }).map((_, index) => {
          const day = index + 1;
          const isDisabled = isPast(day);
          const isRangeBackground = isInRange(day);
          const hasFullRange = startDate && endDate && !isSameDay(startDate, endDate);
          const isDaySelected = isSelected(day);
          const isDayStartDate = isStartDate(day);
          const isDayEndDate = isEndDate(day);
          const isDayToday = isToday(day);

          const handleDayClick = () => {
            if (isDisabled) return;
            const newDate = getCurrentDayDate(day);
            // Jeśli nie ma startDate lub mamy już pełny zakres, zaczynamy od nowa
            const isSelectingStart = !startDate || (startDate !== null && endDate !== null && !isSameDay(startDate, endDate));
            onDateSelect(newDate, isSelectingStart);
          };

          const cellClasses = cn('relative');

          const cellStyle: React.CSSProperties = {};

          // Apply background for range selection
          if (isRangeBackground) {
            cellStyle.backgroundColor = COLORS.rangeBackground;
          }

          // For start date with full range, add gradient on the right half
          if (isDayStartDate && hasFullRange) {
            cellStyle.background = `linear-gradient(to right, transparent 50%, ${COLORS.rangeBackground} 50%)`;
          } 
          // For end date with full range, add gradient on the left half
          else if (isDayEndDate && hasFullRange) {
            cellStyle.background = `linear-gradient(to left, transparent 50%, ${COLORS.rangeBackground} 50%)`;
          }
          // For dates that are both start and end (single date selection)
          else if (isDayStartDate && isDayEndDate) {
            cellStyle.backgroundColor = 'transparent';
          }

          const buttonClasses = cn(
            'relative h-8 w-8 flex items-center justify-center text-xs font-normal mx-auto transition-colors duration-200 rounded-full z-10',
            isDisabled && 'cursor-not-allowed',
            !isDaySelected && !isDisabled && 'hover:bg-gray-100'
          );

          const buttonStyle: React.CSSProperties = {
            color: isDisabled ? COLORS.disabled : COLORS.text,
          };

          if (isDaySelected) {
            buttonStyle.backgroundColor = COLORS.selected;
            buttonStyle.color = 'white';
          } else if (isDayToday && !isRangeBackground) {
            buttonStyle.backgroundColor = COLORS.today;
          }

          return (
            <div key={`day-${day}`} className={cellClasses} style={cellStyle}>
              <button
                type="button"
                onClick={handleDayClick}
                disabled={isDisabled}
                className={buttonClasses}
                style={buttonStyle}
                aria-label={`Select ${MONTH_NAMES[currentDate.getMonth()]} ${day}`}
              >
                {day}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;
